import { useState } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import type { AxiosError } from 'axios';
import {
  loginAtom,
  logoutAtom,
  currentUserAtom,
  isAuthenticatedAtom
} from '@/stores/auth.store';
import { authService } from '@/services/auth.service';

const KIM_ONLY_MESSAGE =
  'You can only sign in with a Kim email address (name@kim.cc). Please use your work account.';

export const useAuth = () => {
  const login = useSetAtom(loginAtom);
  const logout = useSetAtom(logoutAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signIn = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await authService.login(email.trim().toLowerCase(), password);
      login(result);
      return true;
    } catch (err) {
      const status = (err as AxiosError)?.response?.status;
      const message =
        status === 403
          ? KIM_ONLY_MESSAGE
          : (err as Error)?.message || 'Unable to sign in. Please try again.';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = () => {
    logout();
    window.location.href = '/login';
  };

  return { currentUser, isAuthenticated, signIn, signOut, isLoading, error };
};
